import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControlLabel,
  Checkbox,
  FormGroup,
  FormLabel,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import axios from 'axios';

interface Operator {
  id: number;
  name: string;
  code: string;
}

interface Draw {
  id: number;
  gameId: number;
  gameName: string;
  gameBetType: string;
  operatorId: number;
  operatorName: string;
  drawDate: string;
  status: string;
  createdAt: string;
}

const DrawManagement: React.FC = () => {
  const [operators, setOperators] = useState<Operator[]>([]);
  const [draws, setDraws] = useState<Draw[]>([]);
  const [selectedOperators, setSelectedOperators] = useState<number[]>([]);
  const [drawDate, setDrawDate] = useState<Date | null>(new Date());
  const [status, setStatus] = useState<string>('SCHEDULED');
  const [loading, setLoading] = useState(false);
  const [loadingDraws, setLoadingDraws] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [openDialog, setOpenDialog] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  // Fetch operators on component mount
  useEffect(() => {
    fetchOperators();
    fetchAllDraws();
  }, []);

  const fetchOperators = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/operator`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOperators(response.data);
      // Select all operators by default
      const allOperatorIds = response.data.map((operator: Operator) => operator.id);
      setSelectedOperators(allOperatorIds);
    } catch (error) {
      console.error('Error fetching operators:', error);
      setError('Failed to fetch operators');
    }
  };

  const fetchAllDraws = async () => {
    setLoadingDraws(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/draw`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDraws(response.data);
    } catch (error) {
      console.error('Error fetching draws:', error);
      setError('Failed to fetch draws');
    } finally {
      setLoadingDraws(false);
    }
  };

  const handleCreateDraw = async () => {
    if (selectedOperators.length === 0 || !drawDate) {
      setError('Please select operators and draw date');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      
      // Format date as YYYY-MM-DD in local timezone
      const year = drawDate.getFullYear();
      const month = String(drawDate.getMonth() + 1).padStart(2, '0');
      const day = String(drawDate.getDate()).padStart(2, '0');
      const formattedDate = `${year}-${month}-${day}`;
      
      const batchRequest = {
        operatorIds: selectedOperators,
        drawDate: formattedDate,
        status: status
      };

      const response = await axios.post(
        `${API_BASE_URL}/api/draw/batch`,
        batchRequest,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const { createdCount, skippedCount, message } = response.data;
      setSuccess(
        `${message} ${skippedCount > 0 ? `(${skippedCount} already existed)` : ''}`
      );
      setDrawDate(new Date());
      setStatus('SCHEDULED');
      
      await fetchAllDraws();
    } catch (error: any) {
      console.error('Error creating draws:', error);
      if (error.response?.data?.message) {
        setError(error.response.data.message);
      } else {
        setError('Failed to create draws');
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'scheduled':
        return 'primary';
      case 'active':
        return 'success';
      case 'completed':
        return 'default';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const groupDrawsByDate = () => {
    const grouped = draws.reduce((acc, draw) => {
      const drawDate = new Date(draw.drawDate).toDateString();
      if (!acc[drawDate]) {
        acc[drawDate] = {
          drawDate: draw.drawDate,
          operators: [],
          status: draw.status,
          createdAt: draw.createdAt
        };
      }
      
      // Add operator if not already in the list
      if (!acc[drawDate].operators.find((op: any) => op.name === draw.operatorName)) {
        acc[drawDate].operators.push({
          name: draw.operatorName,
          id: draw.operatorId || draw.id
        });
      }
      
      return acc;
    }, {} as Record<string, any>);
    
    return Object.values(grouped);
   };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Draw Management
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" gutterBottom>
          Create and manage lottery draws for different operators and games
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
          {/* Create Draw Form */}
          <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 33%' } }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Create New Draw
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <FormControl component="fieldset">
                    <FormLabel component="legend">Select Operators</FormLabel>
                    <FormGroup>
                      {operators.map((operator) => (
                        <FormControlLabel
                          key={operator.id}
                          control={
                            <Checkbox
                              checked={selectedOperators.includes(operator.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedOperators(prev => [...prev, operator.id]);
                                } else {
                                  setSelectedOperators(prev => prev.filter(id => id !== operator.id));
                                }
                              }}
                            />
                          }
                          label={operator.name}
                        />
                      ))}
                    </FormGroup>
                  </FormControl>

                  {/* Draws will be automatically created for all games */}
                  {selectedOperators.length > 0 && (
                    <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Draws will be created for all games under {selectedOperators.length} selected operator{selectedOperators.length > 1 ? 's' : ''}
                      </Typography>
                    </Box>
                  )}

                  <DatePicker
                    label="Draw Date"
                    value={drawDate}
                    onChange={(newValue) => setDrawDate(newValue)}
                    slotProps={{
                      textField: {
                        fullWidth: true
                      }
                    }}
                  />

                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={status}
                      onChange={(e) => setStatus(e.target.value)}
                      label="Status"
                    >
                      <MenuItem value="SCHEDULED">Scheduled</MenuItem>
                      <MenuItem value="ACTIVE">Active</MenuItem>
                      <MenuItem value="COMPLETED">Completed</MenuItem>
                      <MenuItem value="CANCELLED">Cancelled</MenuItem>
                    </Select>
                  </FormControl>

                  <Button
                    variant="contained"
                    onClick={handleCreateDraw}
                    disabled={loading || selectedOperators.length === 0 || !drawDate}
                    startIcon={loading ? <CircularProgress size={20} /> : <AddIcon />}
                    fullWidth
                  >
                    {loading ? 'Creating...' : 'Create Draws for Selected Operators'}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Box>

          {/* Draws List */}
          <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 67%' } }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    Recent Draws
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={fetchAllDraws}
                    disabled={loadingDraws}
                    startIcon={loadingDraws ? <CircularProgress size={16} /> : null}
                  >
                    Refresh
                  </Button>
                </Box>

                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Draw Date</TableCell>
                        <TableCell>Operators</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Created</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loadingDraws ? (
                        <TableRow>
                          <TableCell colSpan={5} align="center">
                            <CircularProgress />
                          </TableCell>
                        </TableRow>
                      ) : draws.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} align="center">
                            No draws found
                          </TableCell>
                        </TableRow>
                      ) : (
                        groupDrawsByDate().slice(0, 10).map((groupedDraw, index) => (
                          <TableRow key={index}>
                            <TableCell>{formatDate(groupedDraw.drawDate)}</TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {groupedDraw.operators.map((operator: any, opIndex: number) => (
                                  <Chip
                                    key={opIndex}
                                    label={operator.name}
                                    size="small"
                                    variant="outlined"
                                    sx={{ mb: 0.5 }}
                                  />
                                ))}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={groupedDraw.status}
                                color={getStatusColor(groupedDraw.status) as any}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>{formatDate(groupedDraw.createdAt)}</TableCell>
                            <TableCell align="center">
                              <IconButton size="small" color="primary">
                                <ViewIcon fontSize="small" />
                              </IconButton>
                              <IconButton size="small" color="secondary">
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Box>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default DrawManagement;